import { useMemo } from "react";

import { overlayInventorySectorList } from "@/domain/inventory/lib/overlay-inventory-sector-list";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { getInventoryQueryDocument } from "./inventory.gql";

const PROPERTY_PROJECTION_POLL_INTERVAL_MS = 5_000;

export interface UseGetInventoryProps {
  id: string;
  enabled?: boolean;
}

export const useGetInventory = ({ id, enabled = true }: UseGetInventoryProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.inventories.detail(id),
    queryFn: async () => {
      if (!id) {
        throw new Error("Inventory ID is required");
      }
      const client = createOperationClient("GetInventory");
      const data = await client.request(getInventoryQueryDocument, { input: { id } });
      return data.inventory;
    },
    enabled: enabled && !!id,
    staleTime: 60_000,
    refetchOnMount: false,
    refetchInterval: (query) =>
      query.state.data?.propertyProjectionPending ? PROPERTY_PROJECTION_POLL_INTERVAL_MS : false,
  });

  const inventory = useMemo(() => {
    const data = queryResult.data;
    if (!data) return null;
    return overlayInventorySectorList(data);
  }, [queryResult.data]);

  return {
    inventory,
    loading: queryResult.isLoading,
    error: queryResult.error,
    queryResult,
  };
};
